import React, { useEffect, useState } from "react";
import { req, tr, L } from "./api";
import { BiText, Field, Check, Msg } from "./fields";

const blankType = () => ({ name: L(), duration: 30, mode: "Video call", advisers: ["Chairman's office"], buffer: 15, minNoticeHours: 24, bookingWindowDays: 21, active: true });

export default function MeetingsAdmin({ canEdit }) {
  const [types, setTypes] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [edit, setEdit] = useState(null);
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => {
    req("/admin/meeting_types").then(setTypes).catch((e) => setMsg(e.message));
    req("/admin/bookings").then(setBookings).catch((e) => setMsg(e.message));
  };
  useEffect(() => { load(); }, []);

  const saveType = async (e) => {
    e.preventDefault();
    try {
      if (edit === "new") await req("/admin/meeting_types", { method: "POST", body: draft });
      else await req(`/admin/meeting_types/${edit}`, { method: "PUT", body: draft });
      setMsg("Saved."); setEdit(null); setDraft(null); load();
    } catch (x) { setMsg("Could not save: " + x.message); }
  };
  const act = async (b, action) => {
    try {
      let body = { action };
      if (action === "confirm") {
        const link = window.prompt("Video link to include in the confirmation email (optional):", b.videoLink || "");
        if (link) body.videoLink = link;
      }
      if (action === "reschedule") {
        const when = window.prompt("New start time (ISO, e.g. 2026-11-02T15:00:00+06:00):", b.startsAt);
        if (!when) return;
        body.startsAt = new Date(when).toISOString();
      }
      await req(`/admin/bookings/${b.id}`, { method: "PUT", body });
      setMsg("Done — the visitor has been emailed."); load();
    } catch (x) { setMsg("Could not update: " + x.message); }
  };

  if (edit) {
    const d = draft;
    const up = (k, v) => setDraft({ ...d, [k]: v });
    return (
      <form className="form" onSubmit={saveType} data-testid="meeting-type-form">
        <Msg msg={msg} />
        <div className="grp">Meeting type</div>
        <BiText label="Name" value={d.name} onChange={(v) => up("name", v)} />
        <Field label="Duration (minutes)"><select value={d.duration} onChange={(e) => up("duration", Number(e.target.value))}>{[30, 45, 60].map((m) => <option key={m} value={m}>{m}</option>)}</select></Field>
        <Field label="Mode"><input value={d.mode || ""} onChange={(e) => up("mode", e.target.value)} /></Field>
        <Field label="Advisers (one per line)" full><textarea rows={2} value={(d.advisers || []).join("\n")} onChange={(e) => up("advisers", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} /></Field>
        <Field label="Buffer between meetings (min)"><input type="number" value={d.buffer || 0} onChange={(e) => up("buffer", Number(e.target.value))} /></Field>
        <Field label="Minimum notice (hours)"><input type="number" value={d.minNoticeHours || 24} onChange={(e) => up("minNoticeHours", Number(e.target.value))} /></Field>
        <Field label="Booking window (days ahead)"><input type="number" value={d.bookingWindowDays || 21} onChange={(e) => up("bookingWindowDays", Number(e.target.value))} /></Field>
        <Check label="Offered on the site" checked={d.active !== false} onChange={(v) => up("active", v)} testid="mt-active" />
        <div className="row-actions" style={{ gridColumn: "1/-1" }}>
          <button type="submit" className="btn">Save</button>
          <button type="button" className="btn ghost" onClick={() => { setEdit(null); setDraft(null); }}>Cancel</button>
        </div>
      </form>
    );
  }

  if (!types || !bookings) return <p className="muted">Loading…</p>;
  const pill = (s) => <span className={`pill${["confirmed", "completed"].includes(s) ? " on" : ""}${["cancelled"].includes(s) ? " warn" : ""}`}>{s}</span>;
  return (
    <div className="stack" style={{ gap: 40 }}>
      <Msg msg={msg} />
      <div className="stack" style={{ gap: 16 }}>
        <span className="eyebrow">Bookings</span>
        {bookings.length ? (
          <div className="tbl-wrap"><table className="tbl" data-testid="bookings-table">
            <thead><tr><th>When (Dhaka)</th><th>Visitor</th><th>Type</th><th>Adviser</th><th>Status</th>{canEdit && <th></th>}</tr></thead>
            <tbody>{bookings.map((b) => (
              <tr key={b.id}>
                <td className="num">{new Date(b.startsAt).toLocaleString("en-GB", { timeZone: "Asia/Dhaka", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                <td><strong>{b.name}</strong><div className="muted small">{b.email}{b.org ? " · " + b.org : ""}</div>{b.topic && <div className="muted small">{b.topic}</div>}</td>
                <td>{tr(b.typeName)}</td>
                <td>{b.adviser}</td>
                <td>{pill(b.status)}</td>
                {canEdit && <td><div className="row-actions">
                  {b.status === "requested" && <button className="btn sm" onClick={() => act(b, "confirm")} data-testid={`booking-confirm-${b.id}`}>Confirm</button>}
                  {["requested", "confirmed"].includes(b.status) && <button className="btn sm ghost" onClick={() => act(b, "reschedule")} data-testid={`booking-resched-${b.id}`}>Reschedule</button>}
                  {["requested", "confirmed"].includes(b.status) && <button className="btn sm ghost danger" onClick={() => act(b, "cancel")} data-testid={`booking-cancel-${b.id}`}>Cancel</button>}
                  {b.status === "confirmed" && <button className="btn sm ghost" onClick={() => act(b, "complete")}>Mark done</button>}
                </div></td>}
              </tr>))}</tbody></table></div>
        ) : <div className="empty">No bookings yet.</div>}
      </div>
      <div className="stack" style={{ gap: 16 }}>
        <span className="eyebrow">Meeting types</span>
        {canEdit && <div className="row-actions"><button className="btn sm" onClick={() => { setEdit("new"); setDraft(blankType()); }} data-testid="mt-new">Add a meeting type</button></div>}
        <div className="tbl-wrap"><table className="tbl">
          <thead><tr><th>Name</th><th>Duration</th><th>Mode</th><th>Advisers</th><th></th></tr></thead>
          <tbody>{types.map((t) => (
            <tr key={t.id}><td><strong>{tr(t.name)}</strong></td><td className="num">{t.duration} min</td><td>{t.mode}</td><td>{(t.advisers || []).join(", ")}</td>
              <td>{canEdit && <button className="btn sm ghost" onClick={() => { setEdit(t.id); setDraft(JSON.parse(JSON.stringify(t))); }} data-testid={`mt-edit-${t.id}`}>Edit</button>}</td></tr>))}</tbody></table></div>
      </div>
    </div>
  );
}
